import { Box, Typography, useTheme } from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import ExoAvatar from '../exo/ExoAvatar';
import GroupIcon from '@mui/icons-material/Group';
import TagIcon from '@mui/icons-material/Tag';
import PushPinIcon from '@mui/icons-material/PushPin';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { useState, useCallback, useRef, memo } from 'react';

const ChatConversationItemImpl = ({ conversation, isSelected, onSelect, onFileDrop, typingUsers }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounterRef = useRef(0);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer?.types?.includes('Files')) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    dragCounterRef.current = 0;

    const files = e.dataTransfer?.files;
    if (files && files.length > 0 && onFileDrop) {
      onFileDrop(conversation, files);
    }
  }, [conversation, onFileDrop]);

  const getConversationIcon = () => {
    if (conversation.type === 'direct') {
      const otherParticipant = conversation.participants?.find(
        p => p.userId !== conversation.currentUserParticipant?.userId
      );
      // Picture is in user.picture (from UserResource via ChatParticipantResource)
      const pictureUrl = otherParticipant?.user?.picture?.temporaryUrl || otherParticipant?.user?.picture?.url;
      return (
        <ExoAvatar
          size="42px"
          name={conversation.name || t('User')}
          picture={pictureUrl}
        />
      );
    }

    if (conversation.type === 'channel') {
      return (
        <Box
          className="flex items-center justify-center rounded-xl"
          sx={{
            width: 42,
            height: 42,
            backgroundColor: colors.bgInput,
          }}
        >
          <TagIcon sx={{ color: colors.info, fontSize: 20 }} />
        </Box>
      );
    }

    return (
      <Box
        className="flex items-center justify-center rounded-xl"
        sx={{
          width: 42,
          height: 42,
          backgroundColor: colors.bgInput,
        }}
      >
        <GroupIcon sx={{ color: colors.info, fontSize: 20 }} />
      </Box>
    );
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: false, locale: de });
    } catch {
      return '';
    }
  };

  const truncateMessage = (message, maxLength = 35) => {
    if (!message) return '';
    const stripped = message.replace(/<[^>]*>/g, '');
    if (stripped.length <= maxLength) return stripped;
    return stripped.substring(0, maxLength) + '...';
  };

  const hasUnread = conversation.unreadCount > 0;
  const isPinned = conversation.currentUserParticipant?.isPinned;
  const isMuted = conversation.currentUserParticipant?.mutedUntil && new Date(conversation.currentUserParticipant.mutedUntil) > new Date();

  const typingNames = typingUsers ? Object.values(typingUsers) : [];
  const isAnyoneTyping = typingNames.length > 0;
  const typingLabel = (() => {
    if (!isAnyoneTyping) return '';
    if (conversation.type === 'direct' || typingNames.length === 1) {
      return t('{{name}} is typing...', { name: typingNames[0] });
    }
    if (typingNames.length === 2) {
      return t('{{name1}} and {{name2}} are typing...', { name1: typingNames[0], name2: typingNames[1] });
    }
    return t('{{count}} people are typing...', { count: typingNames.length });
  })();

  const handleClick = useCallback(() => {
    onSelect?.(conversation);
  }, [onSelect, conversation]);

  return (
    <Box
      onClick={handleClick}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className="flex items-center gap-3 px-3 py-2 cursor-pointer transition-all rounded-lg mx-1 mb-0.5"
      sx={{
        backgroundColor: isDragOver ? `${colors.info}15` : isSelected ? colors.card : 'transparent',
        border: `1px solid ${isDragOver ? colors.info : isSelected ? colors.info + '40' : 'transparent'}`,
        borderStyle: isDragOver ? 'dashed' : 'solid',
        '&:hover': {
          backgroundColor: isDragOver ? `${colors.info}15` : colors.card,
        },
      }}
    >
      <Box className="relative flex-shrink-0">
        {getConversationIcon()}
      </Box>

      <Box className="flex-1 min-w-0">
        <Box className="flex justify-between items-center gap-2">
          <Box className="flex items-center gap-1 flex-1 min-w-0">
            <Typography
              variant="body2"
              fontWeight={hasUnread ? 600 : 500}
              noWrap
              sx={{
                color: colors.text,
              }}
            >
              {conversation.name || t('Untitled')}
            </Typography>
            {isPinned && (
              <PushPinIcon sx={{ fontSize: 14, color: colors.info, opacity: 0.7, transform: 'rotate(45deg)' }} />
            )}
            {isMuted && (
              <VolumeOffIcon sx={{ fontSize: 14, color: colors.text, opacity: 0.4 }} />
            )}
          </Box>
          <Typography
            variant="caption"
            sx={{
              color: hasUnread ? colors.info : colors.text,
              opacity: hasUnread ? 1 : 0.4,
              whiteSpace: 'nowrap',
              fontSize: '0.7rem',
            }}
          >
            {formatTime(conversation.lastMessageAt)}
          </Typography>
        </Box>

        <Box className="flex justify-between items-center gap-2 mt-0.5">
          {isAnyoneTyping ? (
            <Box className="flex items-center gap-1.5 flex-1 min-w-0">
              <Box className="flex gap-0.5">
                {[0, 1, 2].map((i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 3,
                      height: 3,
                      borderRadius: '50%',
                      backgroundColor: colors.info,
                      animation: 'typing-dot-sidebar 1.4s infinite',
                      animationDelay: `${i * 0.2}s`,
                      '@keyframes typing-dot-sidebar': {
                        '0%, 60%, 100%': { transform: 'translateY(0)', opacity: 0.6 },
                        '30%': { transform: 'translateY(-2px)', opacity: 1 },
                      },
                    }}
                  />
                ))}
              </Box>
              <Typography
                variant="caption"
                noWrap
                sx={{ color: colors.info, fontWeight: 500, fontStyle: 'italic', flex: 1 }}
              >
                {typingLabel}
              </Typography>
            </Box>
          ) : (
            <Typography
              variant="caption"
              noWrap
              sx={{
                color: colors.text,
                opacity: hasUnread ? 0.8 : 0.5,
                fontWeight: hasUnread ? 500 : 400,
                flex: 1,
              }}
            >
              {conversation.latestMessage?.sender?.firstName && (
                <span style={{ opacity: 0.7 }}>
                  {conversation.latestMessage.sender.firstName}:{' '}
                </span>
              )}
              {truncateMessage(conversation.latestMessage?.content) || t('No messages')}
            </Typography>
          )}

          {hasUnread && (
            <Box
              className="flex items-center justify-center rounded-full"
              sx={{
                minWidth: 18,
                height: 18,
                px: 0.5,
                backgroundColor: colors.info,
                color: '#fff',
                fontSize: '0.65rem',
                fontWeight: 600,
              }}
            >
              {conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// Custom equality so a typing event in conversation A doesn't re-render conversation B's item.
// Compare only the fields that actually affect rendering.
const areEqual = (prev, next) => {
  if (prev.isSelected !== next.isSelected) return false;
  if (prev.onSelect !== next.onSelect) return false;
  if (prev.onFileDrop !== next.onFileDrop) return false;
  if (prev.typingUsers !== next.typingUsers) return false;
  const a = prev.conversation;
  const b = next.conversation;
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.id === b.id &&
    a.name === b.name &&
    a.unreadCount === b.unreadCount &&
    a.lastMessageAt === b.lastMessageAt &&
    a.latestMessage?.id === b.latestMessage?.id &&
    a.latestMessage?.content === b.latestMessage?.content &&
    a.currentUserParticipant?.isPinned === b.currentUserParticipant?.isPinned &&
    a.currentUserParticipant?.mutedUntil === b.currentUserParticipant?.mutedUntil
  );
};

export const ChatConversationItem = memo(ChatConversationItemImpl, areEqual);
